import React, { createContext, useContext, useState, useCallback } from "react";
import { apiService } from "../services/api";
import { createStory as createStoryApi } from "../services/dataStories/storiesApi";
import {
  createdStoryPath,
  createdStoryPathAfterError,
  createStoryErrorMessage,
} from "../services/dataStories/createStoryMappers";
import type { ApiVersion } from "../services/api";
import type { CreateStoryRequest } from "../services/dataStories/storiesApi.types";

export interface Story {
  id: number;
  name: string;
  content: any;
  /** Legacy int or Story 1.4 UUID string — matches API */
  workspace_id: number | string;
  build_mode_preferences?: {
    placements?: {
      lg?: any[];
      md?: any[];
      sm?: any[];
      xs?: any[];
      xxs?: any[];
    };
    rowHeight?: number;
    cols?: { [key: string]: number };
    breakpoints?: { [key: string]: number };
    margin?: [number, number];
    containerPadding?: [number, number];
    compactType?: string;
    preventCollision?: boolean;
    allowOverlap?: boolean;
    useCSSTransforms?: boolean;
    isDraggable?: boolean;
    isResizable?: boolean;
    draggableHandle?: string;
  };
}

/** Matches `GET/POST /api/v1/workspaces` after Story 1.4 (UUID id, tenant-scoped). */
export interface Workspace {
  id: string;
  name: string;
  description?: string | null;
  tenant_id?: string | null;
  is_active?: boolean;
}

interface StoryWorkspaceContextType {
  story: Story | null;
  setStory: (story: Story | null) => void;
  workspace: Workspace | null;
  setWorkspace: (workspace: Workspace | null) => void;
  stories: Story[];
  setStories: (stories: Story[]) => void;
  workspaces: Workspace[];
  setWorkspaces: (workspaces: Workspace[]) => void;
  /**
   * `input` carries the create-data-story flow's assembled payload (method,
   * title, goal, audiences). Optional so the existing prompt-less call sites
   * keep working.
   */
  createStory: (
    navigate: any,
    setToast: any,
    input?: CreateStoryRequest,
    version?: ApiVersion,
  ) => Promise<void>;
  updateStory: (updates: Partial<Story>) => Promise<void>;
  updateStoryContent: (content: any) => Promise<void>;
  addAIBlock: (aiBlock: any) => Promise<void>;
  updateAIBlock: (widgetId: string, updates: any) => Promise<void>;
  removeAIBlock: (widgetId: string) => Promise<void>;
}

const StoryWorkspaceContext = createContext<
  StoryWorkspaceContextType | undefined
>(undefined);

export const StoryWorkspaceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [stories, setStories] = useState<Story[]>([]);
  const [story, setStory] = useState<Story | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);

  const createStory = async (
    navigate: any,
    setToast: any,
    input: CreateStoryRequest = {},
    version?: ApiVersion,
  ) => {
    if (!workspace?.id) return;
    try {
      const created = await createStoryApi(workspace.id, input, version);
      // A story with a plan goes to the plan screen; a blank one has none and
      // goes straight to the build page. Anything else has nowhere to go.
      const path = createdStoryPath(workspace.id, input, created);
      if (!path) {
        setToast && setToast({ message: "Story plan was not created. Please try again.", position: "bottom-right", duration: 3000 });
        return;
      }
      navigate(path);
    } catch (error) {
      // A blank create that did not finish (504 or 503) may still have made
      // the story, and says so; open it rather than let a retry make another.
      const recovered = createdStoryPathAfterError(workspace.id, input, error);
      if (recovered) {
        navigate(recovered);
        return;
      }
      setToast &&
        setToast({
          message: createStoryErrorMessage(input, error),
          position: "bottom-right",
          duration: 3000,
        });
    }
  };

  // Centralized story update function
  const updateStory = useCallback(async (updates: Partial<Story>) => {
    setStory(prev => {
      if (!prev?.id) return prev;
      
      // Update local state immediately for responsive UI
      const updatedStory = { ...prev, ...updates };
      
      // Update stories list if needed
      setStories(prevStories => prevStories.map(s => s.id === prev.id ? updatedStory : s));
      
      // Persist to backend
      apiService.put(`/api/v1/stories/${prev.id}`, updates)
        .then(() => console.log("Story updated successfully"))
        .catch(error => console.error("Failed to update story:", error));
      
      return updatedStory;
    });
  }, [setStory, setStories]);

  // Update story content specifically
  const updateStoryContent = useCallback(async (content: any) => {
    await updateStory({ content });
  }, [updateStory]);

  // Add AI Block to story
  const addAIBlock = useCallback(async (aiBlock: any) => {
    setStory(prev => {
      if (!prev?.content || !Array.isArray(prev.content)) {
        console.log("Context: Cannot add AI Block - invalid content", prev);
        return prev;
      }
      
      const updatedContent = [aiBlock, ...prev.content];
      const updatedStory = { ...prev, content: updatedContent };
      
      console.log("Context: AI Block added to local state", { 
        oldLength: prev.content.length, 
        newLength: updatedContent.length,
        aiBlockId: aiBlock.id || aiBlock.vizId
      });
      
      // Persist to backend
      apiService.put(`/api/v1/stories/${prev.id}`, { content: updatedContent })
        .then(() => console.log("Context: AI Block persisted to backend successfully"))
        .catch(error => console.error("Context: Failed to persist AI Block:", error));
      
      return updatedStory;
    });
    
  }, [setStory]);

  // Update AI Block in story
  const updateAIBlock = useCallback(async (widgetId: string, updates: any) => {
    setStory(prev => {
      if (!prev?.content || !Array.isArray(prev.content)) return prev;
      
      const updatedContent = prev.content.map((widget: any) =>
        (widget.vizId || widget.id) === widgetId ? { ...widget, ...updates } : widget
      );
      const updatedStory = { ...prev, content: updatedContent };
      
      // Persist to backend
      apiService.put(`/api/v1/stories/${prev.id}`, { content: updatedContent })
        .then(() => console.log("AI Block updated successfully"))
        .catch(error => console.error("Failed to update AI Block:", error));
      
      return updatedStory;
    });
    
  }, [setStory]);

  // Remove AI Block from story
  const removeAIBlock = useCallback(async (widgetId: string) => {
    setStory(prev => {
      if (!prev?.content || !Array.isArray(prev.content)) {
        console.log("Context: Cannot remove AI Block - invalid content", prev);
        return prev;
      }
      
      const updatedContent = prev.content.filter((widget: any) => 
        (widget.vizId || widget.id) !== widgetId
      );
      const updatedStory = { ...prev, content: updatedContent };
      
      console.log("Context: AI Block removed from local state", { 
        oldLength: prev.content.length, 
        newLength: updatedContent.length,
        removedWidgetId: widgetId
      });
      
      // Persist to backend
      apiService.put(`/api/v1/stories/${prev.id}`, { content: updatedContent })
        .then(() => console.log("Context: AI Block removal persisted to backend successfully"))
        .catch(error => console.error("Context: Failed to persist AI Block removal:", error));
      
      return updatedStory;
    });
    
  }, [setStory]);

  return (
    <StoryWorkspaceContext.Provider
      value={{
        stories,
        setStories,
        story,
        setStory,
        workspaces,
        setWorkspaces,
        workspace,
        setWorkspace,
        createStory,
        updateStory,
        updateStoryContent,
        addAIBlock,
        updateAIBlock,
        removeAIBlock,
      }}
    >
      {children}
    </StoryWorkspaceContext.Provider>
  );
};

export const useStoryWorkspace = () => {
  const context = useContext(StoryWorkspaceContext);
  if (!context) {
    throw new Error(
      "useStoryWorkspace must be used within a StoryWorkspaceProvider"
    );
  }
  return context;
};
